<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import GgSupportHeaderAPI from 'dashboard/api/ggSupportHeader';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: { type: Number, required: true },
  tickets: { type: Array, default: () => [] },
});
const emit = defineEmits(['saved', 'close']);
const { t, te } = useI18n();
const dialog = ref(null);
const query = ref('');
const candidate = ref(null);
const selected = ref([]);
const errorCode = ref('');
const saving = ref(false);
const request = useAbortableRequest();
const MAX_SELECTION = 20;
const candidateStateLabel = computed(() => {
  if (!candidate.value) return '';
  const key = `GG_SUPPORT_HEADER.STATUS['${candidate.value.state}']`;
  return te(key) ? t(key) : candidate.value.state_label;
});
const candidateLinked = computed(() =>
  props.tickets.some(
    ticket => ticket.chamado_id === candidate.value?.chamado_id
  )
);
const candidateSelected = computed(() =>
  selected.value.some(
    ticket => ticket.chamado_id === candidate.value?.chamado_id
  )
);
const errorLabel = computed(() => {
  const key = `GG_SUPPORT_LINKS.ERRORS.${errorCode.value}`;
  return errorCode.value
    ? t(te(key) ? key : 'GG_SUPPORT_LINKS.ERRORS.REQUEST_FAILED')
    : '';
});
watch(query, () => {
  request.abort();
  candidate.value = null;
  errorCode.value = '';
});
const lookup = async () => {
  if (saving.value) return;
  const reference = query.value.trim().toUpperCase();
  const numeric = /^[1-9]\d*$/.test(reference);
  candidate.value = null;
  errorCode.value = '';
  if (
    numeric
      ? Number(reference) > 2147483647
      : !/^[A-Z]{2,10}-[0-9]{1,12}$/.test(reference)
  ) {
    errorCode.value = 'TICKET_REQUIRED';
    return;
  }
  try {
    const response = await request.run(signal =>
      GgSupportHeaderAPI.lookup(
        props.conversationId,
        numeric ? Number(reference) : reference,
        { signal }
      )
    );
    if (response) candidate.value = response.data.ticket;
  } catch (error) {
    errorCode.value = error.response?.data?.code || 'REQUEST_FAILED';
  }
};
const add = () => {
  if (
    !candidate.value ||
    candidateLinked.value ||
    candidateSelected.value ||
    selected.value.length >= MAX_SELECTION
  )
    return;
  selected.value.push(candidate.value);
  query.value = '';
  candidate.value = null;
};
const remove = id => {
  selected.value = selected.value.filter(ticket => ticket.chamado_id !== id);
};
const save = async () => {
  if (saving.value || request.isPending.value || !selected.value.length) return;
  saving.value = true;
  errorCode.value = '';
  try {
    await GgSupportHeaderAPI.link(
      props.conversationId,
      selected.value.map(ticket => ticket.chamado_id)
    );
    useAlert(t('GG_SUPPORT_LINKS.SAVED'));
    emit('saved');
  } catch (error) {
    errorCode.value = error.response?.data?.code || 'REQUEST_FAILED';
  } finally {
    saving.value = false;
  }
};
onMounted(() => dialog.value.open());
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('GG_SUPPORT_LINKS.TITLE')"
    :description="t('GG_SUPPORT_LINKS.DESCRIPTION')"
    :confirm-button-label="t('GG_SUPPORT_LINKS.CONFIRM')"
    :disable-confirm-button="!selected.length || request.isPending.value"
    :is-loading="saving"
    overflow-y-auto
    @confirm="save"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-3">
      <div class="flex items-end gap-2">
        <Input
          id="gg-support-link-id"
          v-model="query"
          :label="t('GG_SUPPORT_LINKS.ID')"
          :placeholder="t('GG_SUPPORT_LINKS.PLACEHOLDER')"
          :disabled="saving"
          autofocus
          class="flex-1 min-w-0"
          @keydown.enter.prevent="lookup"
        />
        <Button
          type="button"
          :label="t('GG_SUPPORT_LINKS.LOOKUP')"
          :disabled="saving || !query.trim()"
          :is-loading="request.isPending.value"
          size="sm"
          @click="lookup"
        />
      </div>
      <p v-if="errorLabel" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ errorLabel }}
      </p>
      <div
        v-if="candidate"
        class="rounded-lg border border-n-weak p-3 flex flex-col gap-2"
      >
        <p class="m-0 text-sm font-medium text-n-slate-12">
          {{
            t('GG_SUPPORT_LINKS.TICKET_LABEL', {
              code: candidate.ticket_code,
              id: candidate.chamado_id,
            })
          }}
        </p>
        <p class="m-0 text-sm text-n-slate-11">{{ candidateStateLabel }}</p>
        <p v-if="candidateLinked" class="m-0 text-sm text-n-slate-11">
          {{ t('GG_SUPPORT_LINKS.ALREADY_LINKED') }}
        </p>
        <Button
          v-else
          type="button"
          :label="t('GG_SUPPORT_LINKS.ADD')"
          :disabled="
            saving || candidateSelected || selected.length >= MAX_SELECTION
          "
          variant="faded"
          size="sm"
          @click="add"
        />
      </div>
      <p role="status" class="m-0 text-sm text-n-slate-11">
        {{
          t('GG_SUPPORT_LINKS.COUNT', {
            count: selected.length,
            max: MAX_SELECTION,
          })
        }}
      </p>
      <ul v-if="selected.length" class="m-0 p-0 list-none flex flex-col gap-2">
        <li
          v-for="ticket in selected"
          :key="ticket.chamado_id"
          class="flex items-center justify-between gap-2 rounded-lg border border-n-weak p-2"
        >
          <span class="text-sm text-n-slate-12">
            {{
              t('GG_SUPPORT_LINKS.TICKET_LABEL', {
                code: ticket.ticket_code,
                id: ticket.chamado_id,
              })
            }}
          </span>
          <Button
            type="button"
            icon="i-lucide-x"
            :aria-label="
              t('GG_SUPPORT_LINKS.REMOVE', { code: ticket.ticket_code })
            "
            :disabled="saving"
            variant="ghost"
            color="slate"
            size="sm"
            @click="remove(ticket.chamado_id)"
          />
        </li>
      </ul>
      <p class="m-0 text-xs text-n-slate-11">
        {{ t('GG_SUPPORT_LINKS.HINT') }}
      </p>
    </div>
  </Dialog>
</template>
